import React from "react";
import { MdMoneyOff } from "react-icons/md";
import Card from "../../../../components/card";
import { UnbilledHoursKpi } from "../../../../interfaces/Dashboard";

/**
 * Vazamento de receita: horas apontadas em projeto por hora sem vínculo com nenhuma OS viva.
 *
 * É o indicador que o portal não tinha e que sai direto em dinheiro. O texto explica a janela e o
 * recorte porque "R$ 40 mil não faturados" sem período nem critério não é acionável.
 */

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtHours = (h: number) =>
  `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;

const UnbilledHoursCard: React.FC<{ data: UnbilledHoursKpi }> = ({ data }) => (
  <Card extra="p-5">
    <div className="mb-1 flex items-center gap-2">
      <MdMoneyOff className="h-5 w-5 text-red-500" aria-hidden="true" />
      <h3 className="text-base font-bold text-navy-700 dark:text-white">Horas não faturadas</h3>
    </div>
    <p className="mb-4 text-xs text-gray-700 dark:text-gray-300">
      Projetos por hora, últimos {data.lookbackMonths} meses, sem vínculo com nenhuma OS ativa.
    </p>

    {data.hourlyRateHours === 0 ? (
      <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Nenhuma hora solta no período.
        </p>
      </div>
    ) : (
      <>
        <div className="flex flex-col gap-3">
          {data.topClients.map((c) => {
            const max = Math.max(...data.topClients.map((x) => x.amount), 1);
            return (
              <div key={c.clientId}>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium text-navy-700 dark:text-white" title={c.clientName}>
                    {c.clientName}
                    <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">
                      · {fmtHours(c.hours)}
                    </span>
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums text-navy-700 dark:text-white">
                    {fmtMoney(c.amount)}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-red-500 to-rose-400 transition-all duration-500"
                    style={{ width: `${(c.amount / max) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {data.affectedClientCount > data.topClients.length && (
          <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
            Mostrando os {data.topClients.length} maiores de {data.affectedClientCount} clientes
            afetados.
          </p>
        )}
      </>
    )}

    {data.projectsWithoutRate.length > 0 && (
      <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
        <strong>Sem taxa/hora cadastrada:</strong> {data.projectsWithoutRate.join(", ")}. As horas
        contam, mas o valor não pode ser calculado.
      </p>
    )}

    {data.otherBillingTypeHours > 0 && (
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        Há ainda {fmtHours(data.otherBillingTypeHours)} em projetos de escopo fechado ou recorrente —
        não entram no valor porque a hora não é vendida avulsa nesses contratos.
      </p>
    )}
  </Card>
);

export default UnbilledHoursCard;
